/**
 * The one place the app learns "the backend is in planned maintenance".
 *
 * **Module scope, deliberately** — the same shape `client.ts`'s own
 * `tokenProvider` uses. The detector that WRITES here (`maintenanceMiddleware.ts`)
 * runs inside `openapi-fetch`, which has no React tree above it and cannot call a
 * hook; the thing that READS here (`app/MaintenanceGate.tsx`) is a component. A
 * plain subscribe/notify pair is the bridge, and `AuthProvider`'s
 * `port.onUserChanged(user => dispatch(...))` is the same bridge for the auth
 * port — a `useReducer` fed by an effect that subscribes to something outside
 * React, never a second copy of the state living in two places.
 *
 * **The shape mirrors `routes/errors/Maintenance.tsx`'s own props**, on purpose
 * kept as a SEPARATE declaration rather than an import from `routes/`.
 * `src/api/` is the client-and-money layer (CLAUDE.md); a route component
 * importing back into it, or this file importing forward into `routes/`, is the
 * layering CLAUDE.md's "Layers" section draws a line around. Structural typing
 * means `<Maintenance {...info} />` still type-checks as long as the two shapes
 * agree — which `MaintenanceGate.test.tsx` and `Maintenance.test.tsx` both
 * exercise, from opposite ends.
 */

export interface MaintenanceCapability {
  label: string
  statusLabel: string
  available: boolean
}

export interface MaintenanceInfo {
  heading: string
  body: string
  returnEta?: string
  capabilities?: MaintenanceCapability[]
  statusUrl?: string
}

export type MaintenanceState = { active: false } | { active: true; info: MaintenanceInfo }

const INACTIVE: MaintenanceState = { active: false }

let state: MaintenanceState = INACTIVE

type Listener = () => void
const listeners = new Set<Listener>()

/** The current state, read synchronously — what a lazy `useReducer` init reads
 *  on first render and what a subscriber re-reads after being notified. */
export function getMaintenanceState(): MaintenanceState {
  return state
}

/**
 * Set or clear the maintenance state.
 *
 * `null` clears it — the 503 middleware's read on any non-503 response, since a
 * request that got a real answer is the one signal available that maintenance
 * has ended. A non-null `MaintenanceInfo` replaces whatever was there, which
 * lets a later 503 update a still-active notice (a changed `returnEta`, say)
 * without the app needing to notice the difference itself.
 */
export function setMaintenance(info: MaintenanceInfo | null): void {
  state = info ? { active: true, info } : INACTIVE
  listeners.forEach((listener) => listener())
}

/** Subscribe to every change. Returns the unsubscribe function, the same shape
 *  `useOnline`'s own window listeners return from their effect. */
export function subscribeMaintenance(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Test seam only — a real app never un-shows maintenance except by the
 *  middleware's own clear path. Exported so a test file does not depend on
 *  import order to get a clean store. */
export function __resetMaintenanceForTests(): void {
  state = INACTIVE
  listeners.clear()
}
